'use client';

import { motion, HTMLMotionProps, Variants } from 'framer-motion';
import { cn } from '@/utils/cn';
import React from 'react';

type MotionDivProps = HTMLMotionProps<'div'>;

export interface AnimationProps extends MotionDivProps {
    delay?: number;
    duration?: number;
    className?: string;
}

export const FadeIn = ({
    children,
    delay = 0,
    duration = 0.5,
    className,
    ...props
}: AnimationProps) => {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration, delay, ease: 'easeOut' }}
            className={cn(className)}
            {...props}
        >
            {children}
        </motion.div>
    );
};

export const SlideUp = ({
    children,
    delay = 0,
    duration = 0.5,
    offset = 20,
    className,
    ...props
}: AnimationProps & { offset?: number }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: offset }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration, delay, ease: 'easeOut' }}
            className={cn(className)}
            {...props}
        >
            {children}
        </motion.div>
    );
};

export const ScaleIn = ({
    children,
    delay = 0,
    duration = 0.5,
    scale = 0.95,
    className,
    ...props
}: AnimationProps & { scale?: number }) => {
    return (
        <motion.div
            initial={{ opacity: 0, scale: scale }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration, delay, ease: 'easeOut' }}
            className={cn(className)}
            {...props}
        >
            {children}
        </motion.div>
    );
};

export const StaggerContainer = ({
    children,
    delay = 0,
    staggerChildren = 0.1,
    className,
    ...props
}: AnimationProps & { staggerChildren?: number }) => {
    return (
        <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={{
                hidden: {},
                visible: {
                    transition: {
                        staggerChildren: staggerChildren,
                        delayChildren: delay,
                    },
                },
            }}
            className={cn(className)}
            {...props}
        >
            {children}
        </motion.div>
    );
};

export const StaggerItem = ({
    children,
    className,
    variants,
    ...props
}: AnimationProps) => {
    const defaultVariants: Variants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: 'easeOut' }
        },
    };

    return (
        <motion.div
            variants={variants || defaultVariants}
            className={cn(className)}
            {...props}
        >
            {children}
        </motion.div>
    );
};
